import type { AdminGymAiCap, AdminGymAiLimits } from '@steado/admin-contract/gyms';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { FieldRow, FieldRows, Panel } from '@/components/panel/panels';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth/auth-context';
import { formatLocalDate } from '@/lib/format';
import { formatUsd } from '@/lib/format-spend';
import { cn } from '@/lib/utils';

import { AiLimitsDialog } from './AiLimitsDialog';
import { CAPS, compactTokens, formatCount, sortTasks, taskLabel, usageLevel } from './gym-ai-limits';
import { gymAiLimitsQueryOptions } from './gym-ai-limits-query';

/** The ink for a figure that is near or past its cap. */
const LEVEL_TEXT = { ok: '', near: 'text-warn-fg', reached: 'text-err-fg' } as const;

interface AiLimitsPanelProps {
  readonly gymId: string;
}

/**
 * What the gym's AI may use, how much of it is used, and what it cost.
 *
 * Reads its own endpoint, so a slow or failed read leaves the rest of the gym
 * page alone. Admins edit the three caps here; support reads.
 */
export function AiLimitsPanel({ gymId }: AiLimitsPanelProps) {
  const { api, can } = useAuth();
  const query = useQuery(gymAiLimitsQueryOptions(api, gymId));
  const [editing, setEditing] = useState(false);

  const limits = query.data;

  return (
    <>
      <Panel
        title="AI limits"
        description="What this gym’s AI can use. A limit set here applies to this gym only."
        bleed
        {...(can.mutate && limits !== undefined
          ? {
              action: (
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={() => {
                    setEditing(true);
                  }}
                >
                  Edit limits
                </Button>
              ),
            }
          : {})}
        footer={
          <span className="text-xs text-muted-foreground">
            {can.mutate
              ? 'A change takes effect at once and is recorded in Admin activity.'
              : 'Changing limits needs the admin role.'}
          </span>
        }
      >
        {query.isPending ? (
          <Block>
            <div aria-busy="true" className="flex flex-col gap-2">
              <span className="sr-only">Loading…</span>
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/3" />
            </div>
          </Block>
        ) : query.isError ? (
          <Block>
            <Alert tone="err">
              <AlertDescription>
                <span>Could not load the AI limits. {query.error.message}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={() => {
                    void query.refetch();
                  }}
                >
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          </Block>
        ) : (
          <LimitsBody limits={query.data} />
        )}
      </Panel>

      {editing && limits !== undefined ? (
        <AiLimitsDialog
          gymId={gymId}
          limits={limits}
          onClose={() => {
            setEditing(false);
          }}
        />
      ) : null}
    </>
  );
}

function LimitsBody({ limits }: { readonly limits: AdminGymAiLimits }) {
  const { monthlyTokens, askDailyTokens, draftsPerMemberWeek, spendThisMonth } = limits;
  const heaviest = askDailyTokens.topToday[0];

  return (
    <>
      <FieldRows>
        <FieldRow
          label={CAPS.monthlyTokens.label}
          value={
            <CapValue
              cap={monthlyTokens}
              flag={monthlyTokens.reached ? 'reached' : monthlyTokens.nearLimit ? 'near' : null}
            />
          }
        />
        <FieldRow
          label={CAPS.askDailyTokens.label}
          value={<CapValue cap={askDailyTokens} flag={askDailyTokens.nearLimit ? 'near' : null} />}
        />
        <FieldRow
          label={CAPS.draftsPerMemberWeek.label}
          value={
            <CapValue
              cap={draftsPerMemberWeek}
              note={draftsPerMemberWeek.effective === 0 ? 'AI drafts are off' : undefined}
            />
          }
        />
      </FieldRows>

      <MonthlyUsage limits={limits} />

      <Block title="Top askers today" aside={`Limit ${compactTokens(askDailyTokens.effective)} each`}>
        {heaviest === undefined ? (
          <p className="text-[13px] text-muted-foreground">No one has used Ask today.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {askDailyTokens.topToday.map((person, index) => (
              <li key={`${person.name}-${String(index)}`} className="flex items-baseline gap-2 text-[13px]">
                <span className="truncate font-medium text-foreground">{person.name}</span>
                <span className="text-xs text-muted-foreground">{person.role}</span>
                <span
                  className={cn(
                    'ml-auto font-mono',
                    LEVEL_TEXT[usageLevel(person.tokens, askDailyTokens.effective)],
                  )}
                >
                  {compactTokens(person.tokens)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Block>

      <Block
        title="Spend this month"
        aside={
          spendThisMonth.byTask.length === 0
            ? undefined
            : formatUsd(spendThisMonth.totalUsd, spendThisMonth.costUnknownCalls > 0)
        }
      >
        {spendThisMonth.byTask.length === 0 ? (
          <p className="text-[13px] text-muted-foreground">No AI calls this month yet.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {sortTasks(spendThisMonth.byTask).map((row) => (
              <li key={row.task} className="flex items-baseline gap-2 text-[13px]">
                <span className="font-medium text-foreground">{taskLabel(row.task)}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {row.calls === 1 ? '1 call' : `${formatCount(row.calls)} calls`} ·{' '}
                  {compactTokens(row.tokens)} tokens
                </span>
                <span className="ml-auto font-mono text-foreground">{formatUsd(row.usd)}</span>
              </li>
            ))}
          </ul>
        )}
        {spendThisMonth.costUnknownCalls > 0 ? (
          <p className="mt-2 text-xs text-muted-foreground">
            {spendThisMonth.costUnknownCalls === 1
              ? '1 call did not report a cost'
              : `${formatCount(spendThisMonth.costUnknownCalls)} calls did not report a cost`}
            , so the spend is at least this much.
          </p>
        ) : null}
      </Block>
    </>
  );
}

function MonthlyUsage({ limits }: { readonly limits: AdminGymAiLimits }) {
  const { monthlyTokens, month } = limits;
  const { used, effective, reached, nearLimit } = monthlyTokens;
  const share = effective <= 0 ? 1 : used / effective;
  const percent = reached ? 100 : Math.min(99, Math.floor(share * 100));
  const text = `${compactTokens(used)} of ${compactTokens(effective)} tokens`;

  return (
    <Block title="This month" aside={`Resets ${formatLocalDate(month.resetsOn)}`}>
      <div className="flex items-baseline justify-between gap-3 text-[13px]">
        <p className="text-foreground">
          <span className="font-mono font-medium">{compactTokens(used)}</span> of{' '}
          <span className="font-mono">{compactTokens(effective)}</span> tokens
        </p>
        <span className="font-mono text-xs text-muted-foreground">{String(percent)}%</span>
      </div>
      <div
        role="progressbar"
        aria-label="AI tokens used this month"
        aria-valuemin={0}
        aria-valuemax={effective}
        aria-valuenow={Math.min(used, effective)}
        aria-valuetext={text}
        className="mt-2 h-1.5 overflow-hidden rounded-full bg-secondary"
      >
        <div
          className={cn(
            'h-full rounded-full',
            reached ? 'bg-err-fg' : nearLimit ? 'bg-warn-fg' : 'bg-primary',
          )}
          style={{ width: `${String(percent)}%` }}
        />
      </div>
      {reached ? (
        <p className="mt-2 text-xs text-err-fg">
          Limit reached. Ask and new AI drafts stop until {formatLocalDate(month.resetsOn)}.
        </p>
      ) : nearLimit ? (
        <p className="mt-2 text-xs text-warn-fg">Near the limit.</p>
      ) : null}
    </Block>
  );
}

interface CapValueProps {
  readonly cap: AdminGymAiCap;
  readonly flag?: 'near' | 'reached' | null;
  readonly note?: string | undefined;
}

/** A cap's effective value, whether it is the default or this gym's own, and a flag when it is close. */
function CapValue({ cap, flag = null, note }: CapValueProps) {
  const isOverride = cap.override !== null;

  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="font-mono">{formatCount(cap.effective)}</span>
      <Badge size="sm" tone={isOverride ? 'accent' : 'mute'}>
        {isOverride ? 'Override' : 'Default'}
      </Badge>
      {isOverride ? (
        <span className="text-xs font-normal text-muted-foreground">default {formatCount(cap.default)}</span>
      ) : null}
      {flag === null ? null : (
        <Badge size="sm" tone={flag === 'reached' ? 'err' : 'warn'}>
          {flag === 'reached' ? 'Reached' : 'Near limit'}
        </Badge>
      )}
      {note === undefined ? null : <span className="text-xs font-normal text-muted-foreground">{note}</span>}
    </span>
  );
}

interface BlockProps {
  readonly title?: string;
  /** Small text on the right of the title. */
  readonly aside?: string | undefined;
  readonly children: ReactNode;
}

/** A ruled section inside the panel, with a small heading. */
function Block({ title, aside, children }: BlockProps) {
  return (
    <div className="border-t border-border px-[18px] py-3.5">
      {title === undefined ? null : (
        <div className="mb-2.5 flex items-baseline justify-between gap-3">
          <h4 className="text-xs font-medium text-muted-foreground">{title}</h4>
          {aside === undefined ? null : (
            <span className="font-mono text-xs text-muted-foreground">{aside}</span>
          )}
        </div>
      )}
      {children}
    </div>
  );
}
